import {
  BaseModel,
  EventWatchable,
  Watchable,
  useInScopeModel,
  type ReadonlyEventWatchable,
  type ReadonlyWatchable,
  type WatchableGetter,
} from '@cotera/client/v0/actions/framework';
import { BroadcastEventType, ResourceType } from '@cotera/api';
import type { QueryClient, QueryKey } from '@tanstack/react-query';
import type {
  TenantedClient,
  WorkflowRunStats,
  WorkflowTriggerView,
} from '@cotera/client/app/etc/api/typed-client';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { isActiveRunStatus } from './workflow-run-status';
import { normalizeRun, type WorkflowRun } from './workflow-run.types';
import { DEFAULT_DATE_RANGE, dateRangeBounds } from './workflow-runs-filters';
import { readWorkflowResource } from './workflow-resource-read';

const RUNS_PAGE_SIZE = 50;
/**
 * Minimum gap (ms) between background runs fetches (broadcasts, polls). A busy
 * webhook workflow broadcasts many times a second and nearly always has a run
 * in progress, so the debounce alone never settles.
 */
const RUNS_REFRESH_MIN_GAP_MS = 2_000;
/** Poll cadence (ms) while any loaded run is still in progress. */
const WORKFLOW_RUNS_POLL_MS = 3_000;
/**
 * Debounce (ms) for event-triggered stats refreshes. Larger than the list's
 * debounce because run-update broadcasts arrive in bursts and the chart does
 * not need to converge as quickly as the list.
 */
const WORKFLOW_RUN_STATS_DEBOUNCE_MS = 1_000;

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/** Hour buckets for windows up to two days, day buckets otherwise. */
function statsBucketForFilters(filters: WorkflowRunsFilters): 'hour' | 'day' {
  if (filters.createdAfter === undefined) {
    return 'day';
  }
  const from = new Date(filters.createdAfter).getTime();
  const to =
    filters.createdBefore !== undefined
      ? new Date(filters.createdBefore).getTime()
      : Date.now();
  return to - from <= 2 * DAY_MS ? 'hour' : 'day';
}

function runStatsEquality(
  a: WorkflowRunStats | null,
  b: WorkflowRunStats | null
): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  if (a.bucket !== b.bucket || a.points.length !== b.points.length) {
    return false;
  }
  return a.points.every((point, i) => {
    const other = b.points[i];
    return (
      point.bucketStart === other?.bucketStart &&
      point.status === other?.status &&
      point.count === other?.count
    );
  });
}

type ResourceUpdatedFilterPayload = {
  resourceType?: string;
  resourceId?: string;
};

export type WorkflowRunsFilters = {
  /** Statuses to include; empty means no status filter. */
  statuses: string[];
  /** ISO timestamp lower bound on created_at (inclusive). */
  createdAfter?: string;
  /** ISO timestamp upper bound on created_at (inclusive). */
  createdBefore?: string;
  /** `trigger_type`s to include (e.g. bucket flushes); omitted/empty = all. */
  triggerTypes?: string[];
};

/** The bits of the workflow definition the runs page reads. */
type WorkflowRunsWorkflowInfo = {
  name: string;
  /** Whether the workflow has a bucket node, i.e. can spawn flush runs. */
  hasBucketNode: boolean;
};

/** Comma-joined, order-independent form of a multi-select filter. */
const csvKey = (values: readonly string[] | undefined): string =>
  [...(values ?? [])].sort().join(',');

export type WorkflowRunsData = {
  runs: WorkflowRun[];
  total: number;
};

/**
 * React Query cache key for one accumulated runs view (pages 1..page) under the
 * given filters. Used to seed the runs watchable from cache on mount and to
 * write each fetch back through, so revisiting the page renders instantly.
 */
function runsCacheKey(
  orgId: string,
  workflowId: string,
  page: number,
  filters: WorkflowRunsFilters
): QueryKey {
  return queryKeys.workflow.runs({
    orgId,
    workflowId,
    limit: page * RUNS_PAGE_SIZE,
    filterKey: runsFilterKey(filters),
  });
}

function runsFilterKey(filters: WorkflowRunsFilters): string {
  return `${csvKey(filters.statuses)}|${filters.createdAfter ?? ''}|${
    filters.createdBefore ?? ''
  }|${csvKey(filters.triggerTypes)}`;
}

/**
 * Lays a freshly fetched first page over the runs already loaded. Rows the head
 * page covers come from it; older rows keep their loaded state. A short head
 * page is the whole list, so nothing older survives.
 */
function mergeRunsHead(
  head: WorkflowRun[],
  loaded: WorkflowRun[]
): WorkflowRun[] {
  const oldestHead = head[head.length - 1];
  if (head.length < RUNS_PAGE_SIZE || oldestHead === undefined) {
    return head;
  }
  const headIds = new Set(head.map((r) => r.id));
  const cutoff = oldestHead.createdAt.getTime();
  return [
    ...head,
    ...loaded.filter(
      (r) => !headIds.has(r.id) && r.createdAt.getTime() <= cutoff
    ),
  ];
}

/**
 * `retryNum` is deliberately not compared: a retry is always a new run row, so
 * the list swapping a superseded attempt for its replacement already shows up as
 * an id change.
 */
function runsDataEquality(
  a: WorkflowRunsData | null,
  b: WorkflowRunsData | null
): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  if (a.total !== b.total || a.runs.length !== b.runs.length) {
    return false;
  }
  for (let i = 0; i < a.runs.length; i++) {
    const x = a.runs[i];
    const y = b.runs[i];
    if (
      x?.id !== y?.id ||
      x?.status !== y?.status ||
      x?.startedAt?.getTime() !== y?.startedAt?.getTime() ||
      x?.finishedAt?.getTime() !== y?.finishedAt?.getTime()
    ) {
      return false;
    }
  }
  return true;
}

type WorkflowRunsModelProps = {
  workflowId: string;
  orgId: string;
  client: Pick<TenantedClient, 'resource'>;
  queryClient: QueryClient;
  /** Override {@link RUNS_REFRESH_MIN_GAP_MS}; tests pass 0. */
  refreshMinGapMs?: number;
};

/**
 * Runs-list view model for one workflow. Its event watchable listens for the
 * workflow's `ResourceUpdated` broadcasts (filtered by workflow-definition id),
 * fetches the runs (accumulating pages), and exposes them as a watchable value
 * the list renders — plus a poll while any loaded run is in progress.
 */
export class WorkflowRunsModel extends BaseModel {
  readonly workflowId: string;
  readonly runsDataWatchable: ReadonlyEventWatchable<WorkflowRunsData | null>;
  readonly filtersWatchable: ReadonlyWatchable<WorkflowRunsFilters>;
  readonly hasMoreWatchable: ReadonlyWatchable<boolean>;
  readonly triggersByIdWatchable: ReadonlyEventWatchable<
    Map<string, WorkflowTriggerView>
  >;
  readonly workflowNameWatchable: ReadonlyWatchable<string | null>;
  /**
   * Whether the workflow has a bucket node. Gates the "bucket flushes" filter,
   * which is noise on a workflow that can never produce a flush run.
   */
  readonly hasBucketNodeWatchable: ReadonlyWatchable<boolean>;
  readonly runStatsWatchable: ReadonlyEventWatchable<WorkflowRunStats | null>;

  private readonly _filters: Watchable<WorkflowRunsFilters>;
  private readonly _page: Watchable<number>;
  private readonly _runsData: ReadonlyEventWatchable<WorkflowRunsData | null>;
  private readonly _runStats: ReadonlyEventWatchable<WorkflowRunStats | null>;
  private readonly _workflowInfo: ReadonlyEventWatchable<WorkflowRunsWorkflowInfo | null>;
  /** The stats panel is lazy; no stats are fetched until it is expanded. */
  private readonly _statsEnabled: Watchable<boolean>;
  private _pollTimer: ReturnType<typeof setInterval> | null = null;
  /**
   * What the runs fetches have built so far, and the filters/page count it was
   * built for. Kept here rather than read back from `_runsData` because the
   * watchable drops results from superseded refreshes, and each fetch extends
   * the previous one.
   */
  private _loaded: WorkflowRunsData | null = null;
  private _loadedFilterKey: string | null = null;
  private _loadedPages = 0;
  /** Runs fetches one at a time so each builds on the last. */
  private _runsChain: Promise<unknown> = Promise.resolve();
  /**
   * The fetch waiting its turn, if any. Refreshes that arrive meanwhile join it
   * rather than queueing their own — it reads filters/page when it starts, so
   * it covers them.
   */
  private _queuedRun: Promise<WorkflowRunsData | null> | null = null;
  private readonly _refreshMinGapMs: number;
  private _lastFetchAt = 0;
  /** Set by user actions so the next fetch skips the background gap. */
  private _urgent = false;
  private _cancelGapWait: (() => void) | null = null;

  constructor(props: WorkflowRunsModelProps) {
    super(`workflow-runs:${props.workflowId}`);
    const { workflowId, orgId, client, queryClient } = props;
    this.workflowId = workflowId;
    this._refreshMinGapMs = props.refreshMinGapMs ?? RUNS_REFRESH_MIN_GAP_MS;
    // Must match the list's default filter UI state; preset cutoffs are
    // rounded (see dateRangeBounds) so this seed key and the list's first
    // fetch key agree across remounts.
    const initialFilters: WorkflowRunsFilters = {
      statuses: [],
      ...dateRangeBounds(DEFAULT_DATE_RANGE),
    };
    this._filters = Watchable.fromValue<WorkflowRunsFilters>(initialFilters);
    this._page = Watchable.fromValue(1);

    const matchesWorkflow = (p: ResourceUpdatedFilterPayload): boolean =>
      p?.resourceType === 'workflow' && p?.resourceId === workflowId;

    // Seed from the React Query cache so a remount shows the previously loaded
    // runs immediately (no loading flash); `runOnMount` then refetches in the
    // background and `fn` writes each result back to the cache for next time.
    const initialCached =
      queryClient.getQueryData<WorkflowRunsData>(
        runsCacheKey(orgId, workflowId, 1, initialFilters)
      ) ?? null;

    // Broadcasts and polls only refetch the first page; loadMore fetches only
    // the pages it adds. Re-walking every loaded page on each run update put
    // one tab with ~44 pages open at ~15 req/s of COUNT + OFFSET scans and
    // pinned the prod DB.
    const fetchNext = async (
      get: WatchableGetter
    ): Promise<WorkflowRunsData | null> => {
      const filters = get(this._filters);
      const { createdAfter, createdBefore } = filters;
      const page = get(this._page);
      const filterKey = runsFilterKey(filters);
      const statusKey = csvKey(filters.statuses);
      const triggerTypeKey = csvKey(filters.triggerTypes);
      const prev = this._loaded;
      const fetchRuns = (offset: number, limit: number) =>
        client.resource.workflow.listRuns({
          workflowId,
          limit,
          offset,
          ...(statusKey !== '' && { status: statusKey }),
          ...(triggerTypeKey !== '' && { triggerType: triggerTypeKey }),
          ...(createdAfter !== undefined && { createdAfter }),
          ...(createdBefore !== undefined && { createdBefore }),
        });

      let data: WorkflowRunsData;
      if (
        prev === null ||
        filterKey !== this._loadedFilterKey ||
        page < this._loadedPages
      ) {
        const res = await fetchRuns(0, page * RUNS_PAGE_SIZE);
        if (res.isErr()) {
          return get(this._runsData);
        }
        data = {
          runs: res.value.items.map(normalizeRun),
          total: res.value.total,
        };
      } else if (page > this._loadedPages) {
        // Offset from what is loaded rather than the page number: runs that
        // arrived since the last fetch shift older rows down.
        const res = await fetchRuns(
          prev.runs.length,
          (page - this._loadedPages) * RUNS_PAGE_SIZE
        );
        if (res.isErr()) {
          return get(this._runsData);
        }
        const loadedIds = new Set(prev.runs.map((r) => r.id));
        data = {
          runs: [
            ...prev.runs,
            ...res.value.items
              .map(normalizeRun)
              .filter((r) => !loadedIds.has(r.id)),
          ],
          total: res.value.total,
        };
      } else {
        const res = await fetchRuns(0, RUNS_PAGE_SIZE);
        if (res.isErr()) {
          return get(this._runsData);
        }
        data = {
          runs: mergeRunsHead(res.value.items.map(normalizeRun), prev.runs),
          total: res.value.total,
        };
      }
      this._loaded = data;
      this._loadedFilterKey = filterKey;
      this._loadedPages = page;
      // Write-through so the next mount can seed instantly from cache.
      queryClient.setQueryData(
        runsCacheKey(orgId, workflowId, page, filters),
        data
      );
      return data;
    };

    this._runsData = EventWatchable.for<
      WorkflowRunsData | null,
      ResourceUpdatedFilterPayload
    >({
      event: BroadcastEventType.ResourceUpdated,
      runOnMount: true,
      debounceMs: 400,
      initialValue: initialCached,
      filter: matchesWorkflow,
      equalityFn: runsDataEquality,
      fn: (_payload, get) => {
        if (this._queuedRun !== null) {
          return this._queuedRun;
        }
        const next = this._runsChain.then(async () => {
          await this._waitForRefreshGap();
          this._queuedRun = null;
          this._urgent = false;
          try {
            return await fetchNext(get);
          } finally {
            this._lastFetchAt = Date.now();
          }
        });
        this._queuedRun = next;
        this._runsChain = next.catch(() => undefined);
        return next;
      },
    });
    this.runsDataWatchable = this._runsData;
    this.filtersWatchable = this._filters;
    this.hasMoreWatchable = Watchable.from((get) => {
      const data = get(this._runsData);
      return data !== null && data.runs.length < data.total;
    });

    this.triggersByIdWatchable = EventWatchable.for<
      Map<string, WorkflowTriggerView>,
      ResourceUpdatedFilterPayload
    >({
      event: BroadcastEventType.ResourceUpdated,
      runOnMount: true,
      debounceMs: 500,
      initialValue: new Map(),
      filter: matchesWorkflow,
      equalityFn: () => false,
      fn: async (_payload, get) => {
        const res = await client.resource.workflow.listTriggers({ workflowId });
        if (res.isErr()) {
          return get(this.triggersByIdWatchable);
        }
        const map = new Map<string, WorkflowTriggerView>();
        for (const t of res.value.triggers) {
          map.set(t.triggerId, t);
        }
        return map;
      },
    });

    // Live stats: refreshed by the same broadcasts as the list (debounced,
    // since run updates arrive in bursts), but only fetches while the stats
    // panel is expanded (`_statsEnabled`).
    this._statsEnabled = Watchable.fromValue(false);
    this._runStats = EventWatchable.for<
      WorkflowRunStats | null,
      ResourceUpdatedFilterPayload
    >({
      event: BroadcastEventType.ResourceUpdated,
      runOnMount: false,
      debounceMs: WORKFLOW_RUN_STATS_DEBOUNCE_MS,
      initialValue: null,
      filter: matchesWorkflow,
      equalityFn: runStatsEquality,
      fn: async (_payload, get) => {
        if (!get(this._statsEnabled)) {
          return get(this._runStats);
        }
        const filters = get(this._filters);
        const { createdAfter, createdBefore } = filters;
        const statusKey = csvKey(filters.statuses);
        const triggerTypeKey = csvKey(filters.triggerTypes);
        const res = await client.resource.workflow.runStats({
          workflowId,
          bucket: statsBucketForFilters(filters),
          ...(statusKey !== '' && { status: statusKey }),
          ...(triggerTypeKey !== '' && { triggerType: triggerTypeKey }),
          ...(createdAfter !== undefined && { createdAfter }),
          ...(createdBefore !== undefined && { createdBefore }),
        });
        if (res.isErr()) {
          return get(this._runStats);
        }
        return res.value;
      },
    });
    this.runStatsWatchable = this._runStats;

    this._workflowInfo = EventWatchable.for<
      WorkflowRunsWorkflowInfo | null,
      ResourceUpdatedFilterPayload
    >({
      event: BroadcastEventType.ResourceUpdated,
      runOnMount: true,
      debounceMs: 500,
      initialValue: null,
      filter: matchesWorkflow,
      equalityFn: (a, b) =>
        a?.name === b?.name && a?.hasBucketNode === b?.hasBucketNode,
      fn: async (_payload, get) => {
        const res = await client.resource.get({
          type: ResourceType.Workflow,
          id: workflowId,
        });
        if (res.isErr() || res.value.type !== 'workflow') {
          return get(this._workflowInfo);
        }
        const graph = readWorkflowResource(res)?.graph;
        return {
          name: res.value.workflow.name,
          hasBucketNode:
            graph?.nodes.some((node) => node.type === 'bucket') ?? false,
        };
      },
    });
    this.workflowNameWatchable = Watchable.from(
      (get) => get(this._workflowInfo)?.name ?? null
    );
    this.hasBucketNodeWatchable = Watchable.from(
      (get) => get(this._workflowInfo)?.hasBucketNode ?? false
    );

    // Poll while any loaded run is in progress; the fn fetches directly so this
    // refetches (status changes converge even if a broadcast was missed).
    this._pollTimer = setInterval(() => {
      const data = this._runsData.snapshot();
      const hasActiveRun =
        data !== null && data.runs.some((run) => isActiveRunStatus(run.status));
      if (hasActiveRun) {
        void this._runsData.refresh();
        void this._runStats.refresh();
      }
    }, WORKFLOW_RUNS_POLL_MS);
  }

  private async _waitForRefreshGap(): Promise<void> {
    const wait = this._lastFetchAt + this._refreshMinGapMs - Date.now();
    if (this._urgent || wait <= 0) {
      return;
    }
    await new Promise<void>((resolve) => {
      const done = () => {
        clearTimeout(timer);
        this._cancelGapWait = null;
        resolve();
      };
      const timer = setTimeout(done, wait);
      this._cancelGapWait = done;
    });
  }

  /** User actions skip the background gap, including one already waiting. */
  private _hurry(): void {
    this._urgent = true;
    this._cancelGapWait?.();
  }

  /** Replace the run filters (resets to the first page) and refetch. */
  setFilters(filters: WorkflowRunsFilters): void {
    this._filters.set(filters);
    this._page.set(1);
    this._hurry();
    void this._runsData.refresh();
    void this._runStats.refresh();
  }

  /**
   * Enable/disable the lazy stats fetch (called when the stats panel is
   * expanded/collapsed). Enabling triggers an immediate fetch; while disabled,
   * refreshes are no-ops but the last value is kept for instant re-expand.
   */
  setStatsEnabled(enabled: boolean): void {
    this._statsEnabled.set(enabled);
    if (enabled) {
      void this._runStats.refresh();
    }
  }

  /**
   * Force-refetch the runs list and stats. The server broadcasts
   * `ResourceUpdated` after a run mutation, but that round-trips through
   * pg_notify and a debounce; refreshing directly makes the change land
   * immediately.
   */
  refresh(): void {
    this._hurry();
    void this._runsData.refresh();
    void this._runStats.refresh();
  }

  /** Load the next page of runs (accumulated into the watchable value). */
  loadMore(): void {
    this._page.set(this._page.snapshot() + 1);
    this._hurry();
    void this._runsData.refresh();
  }

  dispose(): void {
    this._cancelGapWait?.();
    if (this._pollTimer !== null) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
    this._runsData.unsubscribe();
    this._runStats.unsubscribe();
    this.triggersByIdWatchable.unsubscribe();
    this._workflowInfo.unsubscribe();
  }
}

export const useWorkflowRunsModel = (): WorkflowRunsModel => {
  const model = useInScopeModel(WorkflowRunsModel);
  if (model === null) {
    throw new Error('WorkflowRunsModel not found in scope');
  }
  return model;
};
